// Theme management — light / dark / system.
//
// Sets `data-theme="dark"` (or removes the attribute for light) on
// <html> so the CSS variables in globals.css can flip. Persists the
// user's choice in localStorage; on first visit, falls back to the
// browser's prefers-color-scheme.
//
// Why this lives outside React state: the theme attribute needs to
// be applied before first paint to avoid a brief flash of the wrong
// theme. The init function runs synchronously from main.tsx; the
// hook is just for components that need to read or change the theme.

import { useEffect, useState } from "react";

export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "helpdesk:theme";

function readPreference(): ThemePreference {
  if (typeof localStorage === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** True if the resolved theme right now is "dark". */
export function isDarkActive(pref: ThemePreference): boolean {
  if (pref === "dark") return true;
  if (pref === "light") return false;
  return systemPrefersDark();
}

/** Apply the resolved theme to <html>. Idempotent and cheap. */
export function applyTheme(pref: ThemePreference): void {
  if (typeof document === "undefined") return;
  const dark = isDarkActive(pref);
  if (dark) {
    document.documentElement.setAttribute("data-theme", "dark");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

/** Init from localStorage at app startup, before React renders. */
export function initTheme(): void {
  applyTheme(readPreference());
}

/** Hook for components that need to read or mutate the theme. */
export function useTheme(): {
  preference: ThemePreference;
  isDark: boolean;
  setPreference: (p: ThemePreference) => void;
  toggle: () => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference());

  // If preference is "system", react to OS-level prefers-color-scheme
  // changes so the UI flips when the user changes their system theme.
  useEffect(() => {
    if (preference !== "system") return;
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference]);

  const setPreference = (p: ThemePreference) => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, p);
    }
    setPreferenceState(p);
    applyTheme(p);
  };

  const toggle = () => setPreference(isDarkActive(preference) ? "light" : "dark");

  return {
    preference,
    isDark: isDarkActive(preference),
    setPreference,
    toggle,
  };
}
